import { initializeApp, getApps, getApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import firebaseConfig from "../firebase-applet-config.json";

export class AdminAuthError extends Error {}

async function sessionToken(): Promise<string> {
  const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
  const auth = getAuth(app);
  await auth.authStateReady();

  const user = auth.currentUser;
  if (!user) {
    throw new AdminAuthError("Tu sesión de Google no está activa. Tocá \"Salir\" y volvé a ingresar con Google.");
  }
  return user.getIdToken();
}

function checkSessionStatus(status: number): void {
  if (status === 401) {
    throw new AdminAuthError("Sesión vencida. Tocá \"Salir\" y volvé a ingresar con Google.");
  }
  if (status === 403) {
    throw new AdminAuthError("Tu cuenta no tiene permiso para esta sección.");
  }
}

/**
 * fetch para endpoints de administración: agrega el token de Firebase Auth
 * de la sesión de Google activa. El servidor lo valida con requireAdmin().
 */
export async function adminFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const token = await sessionToken();
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(input, { ...init, headers });
  checkSessionStatus(res.status);
  return res;
}

/**
 * PUT de un archivo informando el avance (fetch no lo informa). Mismo token y mismos
 * errores de sesión que adminFetch. Devuelve el estado HTTP y el JSON de la respuesta.
 */
export async function adminUpload(
  url: string,
  body: Blob,
  headers: Record<string, string>,
  onProgress?: (percent: number) => void
): Promise<{ status: number; data: any }> {
  const token = await sessionToken();
  const { status, text } = await new Promise<{ status: number; text: string }>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    for (const [name, value] of Object.entries(headers)) xhr.setRequestHeader(name, value);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText });
    xhr.onerror = () => reject(new TypeError("No hubo conexión con el servidor."));
    xhr.send(body);
  });
  checkSessionStatus(status);
  let data: any = null;
  try {
    data = JSON.parse(text);
  } catch {
    // respuesta sin JSON: quien llama decide por el estado HTTP
  }
  return { status, data };
}
